import { useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';

import { Button } from '@mui/material';

import { sleep } from '@altirllc/shared/utils/functionalUtils';

import type { TModalsContext } from '@/contexts/ModalsProvider/types';

import { extractIds } from '@/utils/stationUtils';

import { FormikForm } from '@/components/formik/FormikForm';
import { FormikSubmit } from '@/components/formik/FormikSubmit';
import { GenericModal } from '@/components/modal/GenericModal';
import { GenericModalClose } from '@/components/modal/GenericModalClose';
import { GenericModalFooter } from '@/components/modal/GenericModalFooter';

import type { TEditMemberSubmit, TMember, TMemberStation } from '@/types/member';

import { editMemberSchema } from '@/validation/member';

import { styles } from '../MemberModal.styles';
import { MemberDetailsFields } from './MemberDetailsField';

type TEditMemberProps = TModalsContext & {
  member: TMember;
  onSuccess: (member: TMember) => void;
};

const stationsToSelect = (stations: TMemberStation[], t: TFunction) =>
  stations.map(({ id, name, ...ids }) => ({
    id,
    title: name,
    details: extractIds(ids, t),
  }));

export const MemberDetails = ({ close, member, onSuccess }: TEditMemberProps) => {
  const { t } = useTranslation();

  const initialValues = useMemo(
    () => ({
      ...member,
      memberAt: member.member ? stationsToSelect(member.memberAt, t) : [],
      adminAt: member.admin ? stationsToSelect(member.adminAt, t) : [],
    }),
    [member, t],
  );

  const closeSelf = () => close('members.details');

  const onSubmit: TEditMemberSubmit = async (patch, { setSubmitting }) => {
    await sleep(500);

    setSubmitting(false);
    onSuccess({ ...member, ...patch } as TMember);
    close('members.details');
  };

  return (
    <GenericModal sx={styles.modal}>
      <FormikForm
        initialValues={initialValues}
        validationSchema={editMemberSchema}
        onSubmit={onSubmit}
        sx={styles.body}
      >
        <GenericModalClose onClick={closeSelf} />

        <MemberDetailsFields member={member} onSuccess={onSuccess} />
        <GenericModalFooter placeContent="end">
          <Button variant="outlined" onClick={closeSelf}>
            {t('common.cancel')}
          </Button>

          <FormikSubmit>{t('common.save')}</FormikSubmit>
        </GenericModalFooter>
      </FormikForm>
    </GenericModal>
  );
};
